import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import snakecaseKeys from 'snakecase-keys';
import _ from 'helpers/no_conflict_lodash';

import { Table } from 'react-bootstrap';

import { clickAsLink } from 'helpers/link_helpers';
import MergeRecordsModal from 'components/modals/merge_records_modal';
import AlertDismissable from 'components/alerts/alert_dismissable';
import { propertyAttributes } from './research_constants';

import 'components/styles/merge_page.scss';

export default class MergeProperties extends React.Component {
  static propTypes = {
    mergeUrl: PropTypes.string.isRequired,
    properties: PropTypes.array.isRequired,
    propertyDefaults: PropTypes.object.isRequired,
    propertyIndex: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    const keys = {};

    this.state = {
      showMergeModal: false,
      showErrorPane: false,
      keys: keys
    };

    this.errors = [];
    this.requiredFields = [
      'address',
      'city',
      'state',
      'zip'
    ];

    this.blacklistedFields = [
      'createdAt',
      'createdBy',
      'databaseId',
      'id',
      'lastUpdatedAt',
      'lastUpdatedBy',
      'modelType',
      'ownerType',
      'ownerUrl',
      'thumbnailUrl',
      'url'
    ];

    propertyAttributes.map((key) => {
      if (this.shouldDisplayRow(key)) {
        keys[key] = null;
      }
    });
  }

  findSelectedProperty = (id) => {
    return this.props.properties.find(property => property.id === id);
  }

  handleCancelModal = () => {
    this.setState({
      showMergeModal: false
    });
  }

  handleErrorPaneDismiss = () => {
    this.setState({ showErrorPane: false });
  }

  handleSubmit = () => {
    const keys = this.state.keys;
    this.requiredFields.map((key) => {
      if (!this.shouldDisplayRow(key)) {
        keys[key] = this.props.properties[0].id;
      }
    });

    return axios.post(this.props.mergeUrl, {
      mappings: snakecaseKeys(keys),
      property_ids: this.props.properties.map(property => property.id)
    }).then((response) => {
      window.location = response.data.nextUrl;
    }).catch((error) => {
      this.errors = error.response.data.errors;
      this.showErrorPane();
      this.handleCancelModal();
    });
  }

  onSelectAll = (e) => {
    const id = parseInt(e.target.value);
    const keys = {};

    propertyAttributes.map((key) => {
      keys[key] = id;
    });

    this.setState({
      keys: keys
    });
  }

  onSelectKey = (id, key) => {
    this.setState(prevState => ({
      keys: {
        ...prevState.keys,
        [key]: parseInt(id)
      }
    }));
  }

  requiredKeysSelected = () => {
    const required = this.requiredFields.every((key) => {
      if (this.shouldDisplayRow(key)) {
        return this.state.keys[key] != null;
      } else {
        return true;
      }
    });

    return required;
  }

  selectedAll = (id) => {
    const selected = Object.keys(this.state.keys).every((key) => {
      return this.state.keys[key] === parseInt(id);
    });
    return selected;
  }

  shouldDisplayRow(key) {
    const { properties, propertyDefaults } = this.props;
    if (this.blacklistedFields.includes(key)) return false;

    const firstProperty = properties[0];
    const value =
      Array.isArray(firstProperty[key])
        ? firstProperty[key].toString()
        : firstProperty[key];

    let retVal = false;
    const defaultVal = propertyDefaults[key];

    properties.map((property, index) => {
      const val = Array.isArray(property[key]) ? property[key].toString() : property[key];
      let isDefault;

      if (!defaultVal) {
        isDefault = [null, '', '--'].includes(val);
      } else {
        isDefault = val === defaultVal;
      }

      if (index > 0 && (value !== val || !isDefault) && value && val) {
        retVal = true;
      }
    });
    return retVal;
  }

  showErrorPane = () => {
    this.setState({ showErrorPane: true });
  }

  showMergeModal = () => {
    this.setState({
      showMergeModal: true
    });
  }

  showPropertyIndex = (e) => {
    clickAsLink(window, this.props.propertyIndex, e);
  }

  render() {
    const disabled = !this.requiredKeysSelected();
    return (
      <div className="container">
        <div className="page-header">
          <div className="clickable preview-link" onClick={this.showPropertyIndex}>
            &lt; Back to Properties
          </div>
          <h3>
            Merge Properties
          </h3>
        </div>
        <div className="container merge-container white border-top-0">
          {this.renderErrorPane()}
          <p className="text-lg">
            In the table below, you will see all conflicting information
            between the properties you have selected to merge. For each
            row, please select which data you would like to keep.
          </p>
          <p className="text-lg">
            Please note that all associations to contacts, comps, activities, etc. will be preserved.
          </p>
          <div>
            <Table className="merge-table">
              <thead>
                {this.renderHeaderColumns()}
              </thead>
              <tbody>
                {this.renderTableContent()}
              </tbody>
            </Table>
            <div className="merge-buttons">
              <button
                className="btn btn-sm btn-cancel"
                onClick={this.showPropertyIndex}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-save left-buffer-md"
                disabled={disabled}
                onClick={this.showMergeModal}
              >
                Merge
              </button>
            </div>
            {this.renderMergeModal()}
          </div>
        </div>
      </div>
    );
  }

  renderHeaderColumns() {
    const propertyHeaders = [];
    this.props.properties.map((property, index) => {
      propertyHeaders.push(
        <th key={index}>
          <input
            checked={this.selectedAll(property.id)}
            className="checkbox--md"
            type="checkbox"
            value={property.id}
            onChange={this.onSelectAll}
          />
          <span className="left-buffer-md"> Property {index + 1} </span>
        </th>
      );
    });

    return (
      <tr>
        <th> Fields </th>
        {propertyHeaders}
        <th> Merged Property </th>
      </tr>
    );
  }

  renderTableContent() {
    const rows = [];
    const keysToDelete = [];
    propertyAttributes.map((key) => {
      if (this.shouldDisplayRow(key)) {
        const propertyCells = [];
        this.props.properties.map((property) => {
          const value = Array.isArray(property[key]) ? property[key].join(', ') : property[key];

          propertyCells.push(
            <td key={property.id + key}>
              <input
                checked={this.state.keys[key] === property.id}
                className="checkbox--md"
                key={key}
                type="checkbox"
                value={property.id}
                onChange={() => this.onSelectKey(property.id, key)}
              />
              <span className="left-buffer-sm"> {value} </span>
            </td>
          );
        });
        const id = this.state.keys[key];
        const property = this.findSelectedProperty(id);
        const newVal = id ? property[key] : '';

        rows.push(
          <tr key={key}>
            <td>
              {_.startCase(key)}
              {this.requiredFields.includes(key) && <span className="required"> * </span> }
            </td>
            {propertyCells}
            <td> {newVal} </td>
          </tr>
        );
      } else keysToDelete.push(key);
    });
    return rows;
  }

  renderMergeModal() {
    return (
      <MergeRecordsModal
        recordType={'Properties'}
        shown={this.state.showMergeModal}
        onCancel={this.handleCancelModal}
        onSubmit={this.handleSubmit}
      />
    );
  }

  renderErrorPane() {
    return (
      <AlertDismissable handleDismiss={this.handleErrorPaneDismiss} shown={this.state.showErrorPane}>
        <strong>Unable to merge properties</strong>
        <ul>
          {this.errors.map((error, index) => <li key={index}>{error}</li>)}
        </ul>
      </AlertDismissable>
    );
  }
}
